import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Globe, TrendingUp, Handshake, Truck, ShieldCheck, Target, ArrowRight, CheckCircle, Building2, MapPin, Users, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const DSB_LOGO = '/images/edited-dsb-logo.png';

const T = {
  badge: { de: 'Holding-Leistung', en: 'Holding Service' },
  badgeSub: { de: 'Erbracht durch DSB Business Solutions GmbH', en: 'Provided by DSB Business Solutions GmbH' },
  heroTitle1: { de: 'Strategische Distribution', en: 'Strategic Distribution' },
  heroTitle2: { de: '& Markteinführung', en: '& Market Launch' },
  heroSub: { de: 'Ihr Partner für den erfolgreichen Markteintritt in der DACH-Region. Wir verbinden internationale Marken mit den anspruchsvollsten Märkten Europas.', en: 'Your partner for successful market entry in the DACH region. We connect international brands with the most demanding markets in Europe.' },
  ctaPartnership: { de: 'Partnerschaft anfragen', en: 'Request Partnership' },
  learnMore: { de: 'Mehr erfahren', en: 'Learn more' },
  providedBy: { de: 'Diese Leistung wird erbracht durch:', en: 'This service is provided by:' },
  overviewLabel: { de: 'Überblick', en: 'Overview' },
  overviewTitle: { de: 'Ihr Tor zum europäischen Markt', en: 'Your Gateway to the European Market' },
  overviewP1: { de: 'Die Basic United Holding GmbH bietet internationalen Marken eine strategische Partnerschaft zur Einführung und Skalierung ihrer Produkte in der DACH-Region. Mit unserem etablierten Vertriebsnetzwerk, unserer kommerziellen Expertise und tiefen regionalen Beziehungen bauen wir langfristige Markensichtbarkeit und nachhaltiges Volumenwachstum auf.', en: 'Basic United Holding GmbH offers international brands a strategic partnership for launching and scaling their products in the DACH region. With our established distribution network, commercial expertise and deep regional relationships, we build long-term brand visibility and sustainable volume growth.' },
  overviewP2: { de: 'Die DACH-Region – Deutschland, Österreich und die Schweiz – repräsentiert einen der lukrativsten und qualitätsorientiertesten Märkte Europas. Hohe Kaufkraft, anspruchsvolle Verbraucher und eine dynamische Handelslandschaft bieten ideale Bedingungen für Premium-Produkte.', en: 'The DACH region – Germany, Austria and Switzerland – represents one of the most lucrative and quality-oriented markets in Europe. High purchasing power, discerning consumers and a dynamic retail landscape offer ideal conditions for premium products.' },
  consumers: { de: 'Konsumenten', en: 'Consumers' },
  euEconomy: { de: 'EU-Wirtschaft', en: 'EU Economy' },
  gdpCombined: { de: 'BIP kombiniert', en: 'Combined GDP' },
  dachRegion: { de: 'DACH-Region', en: 'DACH Region' },
  dachDesc: { de: 'Deutschland, Österreich und die Schweiz bilden einen wirtschaftlich starken und kulturell anspruchsvollen Markt mit hoher Nachfrage nach innovativen, qualitativ hochwertigen Produkten.', en: 'Germany, Austria and Switzerland form an economically strong and culturally demanding market with high demand for innovative, high-quality products.' },
  whyUsLabel: { de: 'Warum wir', en: 'Why Us' },
  whyUsTitle: { de: 'Ihre Vorteile mit Basic United Holding', en: 'Your Advantages with Basic United Holding' },
  whyUsSub: { de: 'Wir agieren nicht nur als Importeur – wir sind Ihr dedizierter Markenentwickler in der DACH-Region.', en: 'We don\'t just act as an importer – we are your dedicated brand developer in the DACH region.' },
  whyUsCards: {
    de: [
      { title: 'Distribution & Marktentwicklung', desc: 'Umfassende Route-to-Market-Strategien, maßgeschneidert auf die Besonderheiten des deutschen, österreichischen und schweizerischen Verbraucherverhaltens und der regulatorischen Rahmenbedingungen.', features: ['Nationale Retail-Abdeckung', 'Logistik-Management', 'Regulatorische Compliance'] },
      { title: 'Strategische Partnerschaften', desc: 'Etablierter, hochrangiger Zugang zu Entscheidungsträgern bei nationalen Einzelhandelsketten, Premium-Gastronomie und schnell wachsenden E-Commerce-Plattformen.', features: ['Key Account Management', 'Handelspartnerschaften', 'E-Commerce-Integration'] },
      { title: 'Lokale Umsetzung', desc: 'Interne Kapazitäten zur Navigation komplexer regionaler Compliance, nahtlosem Logistikmanagement und wirkungsvoller Trade-Marketing-Kampagnen.', features: ['Trade Marketing', 'POS-Aktivierung', 'Merchandising'] },
      { title: 'Langfristiger Markenaufbau', desc: 'Ein Engagement zum Schutz des Markenwerts durch sorgfältige Positionierung, nachhaltige Feldaktivierung und strategische Advocacy.', features: ['Brand Building', 'PR & Kommunikation', 'Consumer Engagement'] },
    ],
    en: [
      { title: 'Distribution & Market Development', desc: 'Comprehensive route-to-market strategies tailored to the specifics of German, Austrian and Swiss consumer behaviour and regulatory frameworks.', features: ['National Retail Coverage', 'Logistics Management', 'Regulatory Compliance'] },
      { title: 'Strategic Partnerships', desc: 'Established high-level access to decision-makers at national retail chains, premium gastronomy and fast-growing e-commerce platforms.', features: ['Key Account Management', 'Trade Partnerships', 'E-Commerce Integration'] },
      { title: 'Local Execution', desc: 'Internal capabilities for navigating complex regional compliance, seamless logistics management and impactful trade marketing campaigns.', features: ['Trade Marketing', 'POS Activation', 'Merchandising'] },
      { title: 'Long-term Brand Building', desc: 'A commitment to protecting brand equity through careful positioning, sustainable field activation and strategic advocacy.', features: ['Brand Building', 'PR & Communications', 'Consumer Engagement'] },
    ],
  },
  scopeLabel: { de: 'Partnerschaftsumfang', en: 'Partnership Scope' },
  scopeTitle: { de: 'Umfassende Markteinführung', en: 'Comprehensive Market Launch' },
  scopeDesc: { de: 'Wir agieren als Ihr dedizierter, lokalisierter Distributions- und Markenentwicklungspartner in der DACH-Region. Unser Partnerschaftsumfang umfasst:', en: 'We act as your dedicated, localised distribution and brand development partner in the DACH region. Our partnership scope includes:' },
  scopeItems: {
    de: ['Komplettes Import- und Supply-Chain-Management', 'Gezielte Retail-Platzierung in allen Kanälen', 'Strategische Gastronomie-Einführung', 'Digitale Commerce-Integration', 'Lokale Marketing- und PR-Aktivitäten', 'Kontinuierliches Performance-Reporting'],
    en: ['Complete import and supply chain management', 'Targeted retail placement across all channels', 'Strategic gastronomy introduction', 'Digital commerce integration', 'Local marketing and PR activities', 'Continuous performance reporting'],
  },
  whyNow: { de: 'Warum jetzt?', en: 'Why Now?' },
  whyNowDesc: { de: 'Der europäische Markt für innovative Produkte erlebt ein beispielloses Momentum. Ein starker früher Markteintritt ist entscheidend für langfristigen Erfolg.', en: 'The European market for innovative products is experiencing unprecedented momentum. A strong early market entry is crucial for long-term success.' },
  whyNowItems: {
    de: [
      { title: 'Wachsender Markt', desc: 'Premium-Segment wächst kontinuierlich' },
      { title: 'Aufgeschlossene Verbraucher', desc: 'Hohe Bereitschaft für neue Produkte' },
      { title: 'Strategische Lage', desc: 'Zugang zu 450M EU-Konsumenten' },
    ],
    en: [
      { title: 'Growing Market', desc: 'Premium segment is continuously growing' },
      { title: 'Open-minded Consumers', desc: 'High willingness for new products' },
      { title: 'Strategic Location', desc: 'Access to 450M EU consumers' },
    ],
  },
  ctaTitle: { de: 'Bereit für die DACH-Region?', en: 'Ready for the DACH Region?' },
  ctaP1: { de: 'Wir schätzen Ihr Unternehmen und sind überzeugt, dass Basic United Holding GmbH die kommerzielle Stärke und strategische Vision besitzt, um Ihre Marke erfolgreich in der DACH-Region zu etablieren.', en: 'We value your company and are convinced that Basic United Holding GmbH has the commercial strength and strategic vision to successfully establish your brand in the DACH region.' },
  ctaP2: { de: 'Wir laden Sie ein, diese Partnerschaft im Detail zu besprechen. Unser Führungsteam ist bereit, ein Einführungsgespräch zu vereinbaren oder Sie persönlich zu besuchen.', en: 'We invite you to discuss this partnership in detail. Our leadership team is ready to schedule an introductory meeting or visit you in person.' },
  ctaContact: { de: 'Kontakt aufnehmen', en: 'Get in Touch' },
  ctaDSB: { de: 'DSB Business kontaktieren', en: 'Contact DSB Business' },
  regards: { de: 'Mit freundlichen Grüßen,', en: 'With kind regards,' },
  mgmt: { de: 'Geschäftsführung', en: 'Management' },
};

export default function StrategischeDistribution() {
  const { language } = useLanguage();
  const whyUsIcons = [Truck, Handshake, Target, TrendingUp];
  const whyNowIcons = [BarChart3, Users, MapPin];

  return (
    <main className="bg-[#0F1A2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0F1A2E] via-[#091422] to-[#060E1A] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#F5A623] rounded-full blur-[150px]" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="bg-[#F5A623]/20 text-[#F5A623] px-4 py-2 text-xs font-bold tracking-wider uppercase rounded-full">
                {T.badge[language]}
              </span>
              <span className="text-white/50 text-sm">{T.badgeSub[language]}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br />
              <span className="text-[#F5A623]">{T.heroTitle2[language]}</span>
            </h1>
            
            <p className="text-xl text-white/70 leading-relaxed mb-10 max-w-2xl">
              {T.heroSub[language]}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/kontakt" 
                className="group bg-[#F5A623] text-[#0F1A2E] px-8 py-4 text-sm font-bold tracking-wider uppercase inline-flex items-center gap-3 hover:bg-white transition-all duration-300 rounded-lg"
              >
                {T.ctaPartnership[language]}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a 
                href="#leistungsumfang"
                className="border border-white/30 text-white px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-white/10 transition-all rounded-lg"
              >
                {T.learnMore[language]}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partner Badge */}
      <section className="py-8 bg-[#0A1220] border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <span className="text-white/40 text-sm">{T.providedBy[language]}</span>
            <div className="flex items-center gap-3">
              <img src={DSB_LOGO} alt="DSB Business Solutions" className="h-10 w-auto" />
              <span className="text-[#0F1A2E] font-semibold">DSB Business Solutions GmbH</span>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.overviewLabel[language]}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {T.overviewTitle[language]}
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                {T.overviewP1[language]}
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                {T.overviewP2[language]}
              </p>
              
              <div className="grid grid-cols-3 gap-6">
                {[
                  { num: '83M+', label: T.consumers[language] },
                  { num: 'Top 5', label: T.euEconomy[language] },
                  { num: '€2.5T', label: T.gdpCombined[language] },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-4 bg-[#0A1220] rounded-xl border border-[#1e3a5f]">
                    <span className="text-2xl font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.num}</span>
                    <p className="text-xs text-white/40 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#0F1A2E] to-[#060E1A] rounded-2xl p-10 text-white">
                <Globe size={48} className="text-[#F5A623] mb-6" />
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.dachRegion[language]}</h3>
                <p className="text-white/70 mb-8">
                  {T.dachDesc[language]}
                </p>
                
                <div className="space-y-4">
                  {[
                    { country: 'Deutschland', pop: '84 Mio.', gdp: '€4.0T' },
                    { country: 'Österreich', pop: '9 Mio.', gdp: '€447B' },
                    { country: 'Schweiz', pop: '8.7 Mio.', gdp: '€741B' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                      <span className="font-medium">{item.country}</span>
                      <div className="flex gap-6 text-sm text-white/60">
                        <span>{item.pop}</span>
                        <span className="text-[#F5A623]">{item.gdp}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section id="leistungsumfang" className="py-24 bg-[#0A1220]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.whyUsLabel[language]}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.whyUsTitle[language]}</h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">{T.whyUsSub[language]}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {T.whyUsCards[language].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#152238] rounded-2xl p-8 border border-[#1e3a5f] hover:shadow-xl hover:shadow-[#0F1A2E]/5 transition-all duration-300">
                <div className="w-14 h-14 bg-[#0F1A2E]/5 rounded-xl flex items-center justify-center mb-6">
                  {(() => { const Icon = whyUsIcons[i]; return <Icon size={28} className="text-[#F5A623]" />; })()}
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-white/60 mb-6 leading-relaxed">{item.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {item.features.map((feature, j) => (
                    <span key={j} className="bg-[#0A1220] text-white/40 px-3 py-1 text-xs rounded-full border border-[#1e3a5f]">{feature}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Scope */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.scopeLabel[language]}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.scopeTitle[language]}</h2>
              <p className="text-white/60 leading-relaxed mb-8">{T.scopeDesc[language]}</p>
              <div className="space-y-4">
                {T.scopeItems[language].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#F5A623] flex-shrink-0" />
                    <span className="text-[#F5A623] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-[#0A1220] rounded-2xl p-8 border border-[#1e3a5f]">
                <h3 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.whyNow[language]}</h3>
                <p className="text-white/60 leading-relaxed mb-6">{T.whyNowDesc[language]}</p>
                <div className="space-y-6">
                  {T.whyNowItems[language].map((item, i) => {
                    const Icon = whyNowIcons[i];
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-[#F5A623]/10 rounded-lg flex items-center justify-center flex-shrink-0"><Icon size={20} className="text-[#F5A623]" /></div>
                        <div><h4 className="font-semibold text-white">{item.title}</h4><p className="text-sm text-white/40">{item.desc}</p></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Building2 size={48} className="text-[#F5A623] mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.ctaTitle[language]}</h2>
            <p className="text-lg text-white/70 mb-6 max-w-2xl mx-auto">{T.ctaP1[language]}</p>
            <p className="text-white/50 mb-10">{T.ctaP2[language]}</p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Link to="/kontakt" className="inline-flex items-center gap-3 bg-[#F5A623] text-[#0F1A2E] px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-white transition-all duration-300 rounded-lg">
                {T.ctaContact[language]} <ArrowRight size={18} />
              </Link>
              <Link to="/dsb-business/kontakt" className="inline-flex items-center gap-3 bg-white/10 text-white px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-white hover:text-[#0F1A2E] transition-all duration-300 rounded-lg border border-white/20">
                {T.ctaDSB[language]}
              </Link>
            </div>
            <div className="border-t border-white/10 pt-10">
              <p className="text-white/40 text-sm mb-4">{T.regards[language]}</p>
              <p className="text-white font-semibold text-lg">{T.mgmt[language]}</p>
              <p className="text-[#F5A623]">Mr. Alessandro Costantino & Mr. Admir Basic</p>
              <p className="text-white/60 text-sm mt-2">Basic United Holding GmbH</p>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
